import SlideOverModal from 'Layout/SlideOverModal'
import { useForm, Controller } from 'react-hook-form'
import FormControl from 'Design/components/FormControl'
import FormErrorText from 'Design/components/FormErrorText'
import Label from 'Design/components/Label'
import Button from 'Design/components/Button'
import ReactSelect from 'Src/b/components/ReactSelect'
import { useLazyGetRelevantContentQuery, useUpdateAITrainingFeedbackMutation } from '../../trainingWithAI.service'
import styles from './EditModal.module.scss'
import toast from 'react-hot-toast'
import parseErrorMessage from 'Utils/error-message-parser'
import { ADMIN_FEEDBACK_DROPDOWN_OPTIONS, ADMIN_FEEDBACK_VALUE } from 'Src/constants'
import ReadOnlyInfo from './ReadOnlyInfo'
import { yupResolver } from '@hookform/resolvers/yup'
import validationSchema from './validationSchema'

const EditModal = ({ onHide, ...props }) => {
  const {
    data: { admin_feedback, relevant_content_selected_by_admin }
  } = props

  const [getRelevantContent, getRelevantContentResult] = useLazyGetRelevantContentQuery()
  const [updateAITrainingFeedback, updateAITrainingFeedbackResult] = useUpdateAITrainingFeedbackMutation()

  const formMethods = useForm({
    defaultValues: {
      admin_feedback,
      relevant_content: relevant_content_selected_by_admin
    },
    resolver: yupResolver(validationSchema)
  })
  const {
    handleSubmit,
    formState: { errors },
    watch,
    control
  } = formMethods

  const loadRelevantContentOptions = (inputValue, cb) => {
    const query = {
      title: inputValue
    }
    getRelevantContent(query)
      .unwrap()
      .then((data) => cb(data))
  }

  const handleFormSubmit = (formValue) => {
    const { admin_feedback, relevant_content } = formValue
    const { prediction_id, content_group_id } = props

    const input = { prediction_id, content_group_id, admin_feedback, relevant_content_id: relevant_content?.id }
    const promise = updateAITrainingFeedback(input).unwrap()

    toast.promise(promise, {
      loading: 'Updating training feedback...',
      success: 'Training feedback updated successfully',
      error: parseErrorMessage('Failed to update training feedback. Try again later...')
    })

    promise.then(onHide).catch(() => {})
  }
  const showGetReleventContent = watch('admin_feedback') === ADMIN_FEEDBACK_VALUE.NOT_RELEVANT

  return (
    <SlideOverModal title='Content Feedback' onHide={onHide}>
      <ReadOnlyInfo {...props} />
      <div className={styles.Divider} />

      <form className={styles.Form} onSubmit={handleSubmit(handleFormSubmit)}>
        <FormControl isInvalid={errors.admin_feedback}>
          <Label>Content Feedback</Label>
          <Controller
            control={control}
            name='admin_feedback'
            render={({ field }) => {
              return (
                <ReactSelect
                  {...field}
                  value={ADMIN_FEEDBACK_DROPDOWN_OPTIONS.find(({ value }) => field.value === value)}
                  onChange={({ value }) => field.onChange(value)}
                  isClearable={false}
                  options={ADMIN_FEEDBACK_DROPDOWN_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                />
              )
            }}
          />

          {errors.admin_feedback && <FormErrorText>{errors.admin_feedback.message}</FormErrorText>}
        </FormControl>

        {showGetReleventContent && (
          <FormControl isInvalid={errors.relevant_content}>
            <Label>Relevant content for the visitor question</Label>
            <Controller
              control={control}
              name='relevant_content'
              shouldUnregister
              render={({ field }) => {
                return (
                  <ReactSelect
                    {...field}
                    getOptionLabel={(option) => option.title}
                    getOptionValue={(option) => option.id}
                    placeholder='Type to search...'
                    isClearable={false}
                    options={[]}
                    loadOptions={loadRelevantContentOptions}
                    isLoading={getRelevantContentResult.isLoading}
                    loadingMessage={() => 'Searching for content...'}
                    minCharsBeforeLoadingOptions={3}
                    noOptionsMessage={({ inputValue }) => {
                      if (inputValue.length < 3) return 'Type alteast 3 characters to start searching'
                      return `No content found for input "${inputValue}"`
                    }}
                  />
                )
              }}
            />
            {errors.relevant_content && <FormErrorText>{errors.relevant_content.message}</FormErrorText>}
          </FormControl>
        )}
        <div className={styles.Footer}>
          <Button type='submit' disabled={updateAITrainingFeedbackResult.isLoading} variant='primary'>
            Save
          </Button>
          <Button type='button' variant='link-muted' onClick={onHide}>
            Cancel
          </Button>
        </div>
      </form>
    </SlideOverModal>
  )
}

export default EditModal
